/**
 * What asks first's words (slice 3 §3.1, §8; Paper board F and 43c), apart
 * from the page so a test can read them, and who sees the links. Each
 * action's name, line and group are Composio's (the route's `name`, `line`,
 * `group`): Crew writes none of them.
 */
import { createContext } from "react";
import { shortDate } from "./integrations-view";

/** One write action, as `GET /api/integrations/actions` lists it. */
export type ActionRow = {
  action: string;
  app: string;
  app_name: string;
  title: string;
  label: "write" | "full";
  /** The app's own name for the level ("Read and send"), or null (an MCP tool). */
  level_name: string | null;
  ask_first: boolean;
  default_ask_first: boolean;
  locked: boolean;
  /** The owner changed it from Crew's default: who, and when. */
  changed: { at: string; by: { id: string; name: string | null } | null } | null;
  /** Composio's name for it, in sentence case ("Send email"). */
  name: string;
  /** The first sentence of Composio's description for people. */
  line: string;
  /** By Composio's tags. */
  group: WriteGroup;
};

/** A write's group, by Composio's tags (`worker/integrations/catalog/for-people.ts`). */
export type WriteGroup = "deletes" | "changes" | "adds";

/** A catalog app on the page, connected or not. */
export type AskApp = {
  app: string;
  name: string;
  icon: string | null;
  mark: string;
  connected: boolean;
  /** Its actions that only read: they never ask. */
  reads: number;
  changed: number;
};

export type ActionsPage = { apps: AskApp[]; actions: ActionRow[] };

/**
 * Apart from the integrations list's key: a change there (a disconnect)
 * waits on its refetch, and the row it was for mustn't go first. A tool
 * turned on reads it again (`ToolAskFirst`); the page reads it on mount.
 */
export const actionsKey = ["settings", "ask-first"] as const;

/** Whether the links show: the owner's, where apps are available (§6: the route is the owner's). */
export const AskFirstOwner = createContext(false);

/** The link's words: "What asks first (7)", every write that asks, locked or turned on. */
export function linkText(rows: readonly ActionRow[]): string {
  const n = rows.filter((r) => r.ask_first).length;
  return n ? `What asks first (${n})` : "What asks first";
}

/** The app list's note: "1 change", else "Defaults". */
export const appNote = (a: AskApp) =>
  a.changed ? `${a.changed} ${a.changed === 1 ? "change" : "changes"}` : "Defaults";

/** Under a changed row: "Maya changed this, Oct 23". */
export function changedLine(r: ActionRow, timeZone: string): string | null {
  if (!r.changed) return null;
  const who = r.changed.by?.name?.split(/\s+/)[0] ?? "The owner";
  return `${who} changed this, ${shortDate(r.changed.at, timeZone)}`;
}

/** The groups after Crew's own, in the order the page shows them. */
const GROUPS: { group: WriteGroup; title: string }[] = [
  { group: "deletes", title: "Deletes something" },
  { group: "changes", title: "Changes something" },
  { group: "adds", title: "Adds something" },
];

/**
 * An app's writes as board F groups them: Crew's default holds (always
 * shown first), then the rest by Composio's group, the empty ones left out.
 */
export function groupsOf(rows: readonly ActionRow[]) {
  const rest = rows.filter((r) => !r.locked);
  return {
    always: rows.filter((r) => r.locked),
    groups: GROUPS.map((g) => ({ ...g, rows: rest.filter((r) => r.group === g.group) })).filter(
      (g) => g.rows.length > 0,
    ),
  };
}

/** Beside "Show 8 more", and a closed group's line: "All just do it", "2 show you first". */
export function moreNote(rows: readonly ActionRow[]): string {
  const asking = rows.filter((r) => r.ask_first).length;
  if (asking === 0) return "All just do it";
  if (asking === rows.length) return rows.length === 1 ? "Shows you first" : "All show you first";
  return `${asking} show you first`;
}
